/** @file useHashRoute: keeps `location.hash` and the store's `route` in step, both ways. */
import { useEffect } from 'react';
import type { EditorStore } from '../../state/editorTypes';
import { parseHash } from './parseHash';
import { routeHash } from './routeHash';

/** The window slice the hook touches (a test can pass a fake). */
export type HashWindow = Pick<Window, 'addEventListener' | 'removeEventListener'> & { location: { hash: string } };

/**
 * On mount the hash wins (a pasted link opens its screen); afterwards a `hashchange` navigates the store and a store
 * route change rewrites the hash. Writing the hash it already has is skipped, so the two never ping-pong.
 */
export function useHashRoute(store: EditorStore, win: HashWindow = window): void {
  useEffect(() => {
    const fromHash = (): void => {
      const route = parseHash(win.location.hash);
      if (routeHash(route) !== routeHash(store.getState().route)) store.getState().navigate(route);
    };
    fromHash();
    const unsubscribe = store.subscribe((s, prev) => {
      if (s.route === prev.route) return;
      const hash = routeHash(s.route);
      if (win.location.hash !== hash) win.location.hash = hash;
    });
    win.addEventListener('hashchange', fromHash);
    return () => {
      unsubscribe();
      win.removeEventListener('hashchange', fromHash);
    };
  }, [store, win]);
}
